import {
  checkElementAccess,
  checkPageAccess,
} from "@project/shared/src/utils/ipUtils"
import { useRouter } from "next/router"
import { useEffect, useState } from "react"
import { ipRestrictionConfig } from "../config/ipRestrictionConfig"

/**
 * 統合IP制限フック
 * ページアクセス制限と要素レベルの制限の両方に対応
 * @param elementName - 要素名（指定時は要素制限、未指定時はページ制限）
 * @returns IP制限の状態と制限チェック関数
 */
export const useIPRestriction = (
  elementName?: string
): {
  canAccess: boolean
  clientIP: string
  loading: boolean
  checkTargetPageAccess: (targetPath: string) => Promise<boolean>
} => {
  const [canAccess, setCanAccess] = useState(false)
  const [clientIP, setClientIP] = useState<string>("")
  const [loading, setLoading] = useState(true)

  const pathname = useRouter().pathname
  const pagePath = getPagePath(pathname)

  useEffect(() => {
    // IIFE（即時実行関数）を使用
    ;(async () => {
      try {
        setLoading(true)

        // サーバーサイドからIPアドレスを取得
        const res = await fetch("/api/get-client-ip")
        const data = await res.json()

        const ip = data.clientIP || "unknown"
        setClientIP(ip)

        // 要素名が指定されている場合は要素レベルの制限チェック
        if (elementName) {
          const isElementAccessAllowed = checkElementAccess(
            ip,
            pagePath,
            elementName
          )
          setCanAccess(isElementAccessAllowed)
        } else {
          // 要素名が指定されていない場合はページ制限チェック
          const isPageAccessAllowed = checkPageAccess(
            ip,
            pagePath,
            ipRestrictionConfig.pages
          )
          setCanAccess(isPageAccessAllowed)
        }
      } catch (error) {
        // エラー時は制限をかける（安全側に倒す）
        setCanAccess(false)
        setClientIP("unknown")
      } finally {
        setLoading(false)
      }
    })()
  }, [pagePath, elementName])

  // 遷移先ページのアクセス制限をチェックする関数
  const checkTargetPageAccess = async (
    targetPath: string
  ): Promise<boolean> => {
    try {
      const targetPagePath = getPagePath(targetPath)

      // 既にIPアドレスを取得済みの場合は再利用
      if (clientIP && clientIP !== "unknown") {
        const isTargetPageAccessAllowed = checkPageAccess(
          clientIP,
          targetPagePath,
          ipRestrictionConfig.pages
        )
        return isTargetPageAccessAllowed
      }

      // IPアドレスが未取得の場合は新たに取得
      const res = await fetch("/api/get-client-ip")
      const data = await res.json()
      const ip = data.clientIP || "unknown"

      const isTargetPageAccessAllowed = checkPageAccess(
        ip,
        targetPagePath,
        ipRestrictionConfig.pages
      )
      return isTargetPageAccessAllowed
    } catch (error) {
      return false // エラー時は制限をかける
    }
  }

  return {
    canAccess,
    clientIP,
    loading,
    checkTargetPageAccess,
  }
}

function getPagePath(pathname: string): string {
  const pagePath = pathname.split("/").filter(Boolean).join("/")
  return pagePath
}
